import {
  ClientDeviceRow,
  DeviceCountAssignmentType,
  DEVICE_COUNT_ASSIGNMENT_TYPES,
} from "@/data/models/ClientAdministrationRow";
import { matchesGroupedSelection, orderedSelection } from "@/utils/GroupedFilterSelection";

/**
 * The device list's multi-filter, built like the Mandanten list's on the shared rule in
 * `GroupedFilterSelection.ts` (any ticked option within a group, all groups with a tick across). The groups mirror the Geräte card, so every
 * number there opens the list as a preset of ticked options.
 */
export enum DeviceListFilterGroup {
  KIND = "KIND",
  PANEL_TYPE = "PANEL_TYPE",
  PERIOD = "PERIOD",
  ASSIGNMENT = "ASSIGNMENT",
}

export enum DeviceKindOption {
  /** Every panel, of any panel type (the card's Panels column). */
  PANELS = "PANELS",
  BOXES = "BOXES",
  MINIS = "MINIS",
}

export enum PanelTypeOption {
  HYDRY_PANEL = "HYDRY_PANEL",
  HYDRY_SLIM = "HYDRY_SLIM",
  HYDRY_EDGE = "HYDRY_EDGE",
}

export enum PeriodOption {
  /** Counted under one of the six types today: the card's type rows and Gesamt. */
  CURRENT = "CURRENT",
  /** No open assignment and at least one ended: the Ehemalige Panels row. */
  FORMER = "FORMER",
}

/** One tickable option. The four vocabularies use distinct values, so one array holds a selection. */
export type DeviceListOption = DeviceKindOption | PanelTypeOption | PeriodOption | DeviceCountAssignmentType;

/** Every option in menu order, with its group. */
export const DEVICE_LIST_OPTIONS: Array<{ option: DeviceListOption; group: DeviceListFilterGroup }> = [
  ...Object.values(DeviceKindOption).map((option) => ({ option, group: DeviceListFilterGroup.KIND })),
  ...Object.values(PanelTypeOption).map((option) => ({ option, group: DeviceListFilterGroup.PANEL_TYPE })),
  ...Object.values(PeriodOption).map((option) => ({ option, group: DeviceListFilterGroup.PERIOD })),
  ...DEVICE_COUNT_ASSIGNMENT_TYPES.map((option) => ({ option, group: DeviceListFilterGroup.ASSIGNMENT })),
];

export function deviceListOptionGroup(option: DeviceListOption): DeviceListFilterGroup | null {
  return DEVICE_LIST_OPTIONS.find((entry) => entry.option === option)?.group ?? null;
}

const PANEL_TYPE_OF_OPTION: Record<PanelTypeOption, number> = {
  [PanelTypeOption.HYDRY_PANEL]: 0,
  [PanelTypeOption.HYDRY_SLIM]: 1,
  [PanelTypeOption.HYDRY_EDGE]: 2,
};

/** One ticked option on its own. Uses the counts' own classification, nothing re-derived. */
function matchesOption(row: ClientDeviceRow, option: DeviceListOption): boolean {
  switch (option) {
    case DeviceKindOption.PANELS:
      return row.kind === "panel";
    case DeviceKindOption.BOXES:
      return row.kind === "comBox";
    case DeviceKindOption.MINIS:
      return row.kind === "mini";
    case PanelTypeOption.HYDRY_PANEL:
    case PanelTypeOption.HYDRY_SLIM:
    case PanelTypeOption.HYDRY_EDGE:
      return row.kind === "panel" && row.panelType === PANEL_TYPE_OF_OPTION[option];
    case PeriodOption.CURRENT:
      return row.countedType !== null;
    case PeriodOption.FORMER:
      return row.former;
    default:
      return row.countedType === option;
  }
}

/** Whether a device passes the ticked options: any within a group, all groups with a tick. */
export function matchesDeviceListSelection(row: ClientDeviceRow, selection: DeviceListOption[]): boolean {
  return matchesGroupedSelection(row, selection, deviceListOptionGroup, matchesOption);
}

/** A case-insensitive substring of the serial number; an empty or blank query matches everything. */
export function matchesDeviceSearch(row: ClientDeviceRow, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  return needle === "" || row.serialNumber.toLocaleLowerCase().includes(needle);
}

/** The Android list's sort options. */
export enum DeviceListSort {
  SERIAL_NUMBER = "SERIAL_NUMBER",
  ASSIGNMENT_TYPE = "ASSIGNMENT_TYPE",
  ASSIGNMENT_DATE = "ASSIGNMENT_DATE",
}

export const DEVICE_LIST_SORTS: DeviceListSort[] = Object.values(DeviceListSort);

function bySerial(a: ClientDeviceRow, b: ClientDeviceRow): number {
  return a.serialNumber.localeCompare(b.serialNumber, "de", { numeric: true });
}

function typeRank(row: ClientDeviceRow): number {
  const index = row.countedType ? DEVICE_COUNT_ASSIGNMENT_TYPES.indexOf(row.countedType) : -1;
  return index === -1 ? DEVICE_COUNT_ASSIGNMENT_TYPES.length : index;
}

/**
 * Filter, search and sort; the input is not modified. Assignment type follows the Geräte card's row
 * order with uncounted devices last; assignment date is the current assignment's start, newest
 * first, devices without one last; ties fall back to the serial number (natural order).
 */
export function selectDeviceListRows(
  rows: ClientDeviceRow[],
  selection: DeviceListOption[],
  query: string,
  sort: DeviceListSort
): ClientDeviceRow[] {
  const selected = rows.filter((row) => matchesDeviceListSelection(row, selection) && matchesDeviceSearch(row, query));

  return selected.sort((a, b) => {
    if (sort === DeviceListSort.ASSIGNMENT_TYPE) {
      return typeRank(a) - typeRank(b) || bySerial(a, b);
    }

    if (sort === DeviceListSort.ASSIGNMENT_DATE) {
      const aStart = a.currentAssignment?.startMillis ?? -Infinity;
      const bStart = b.currentAssignment?.startMillis ?? -Infinity;
      return bStart - aStart || bySerial(a, b);
    }

    return bySerial(a, b);
  });
}

/** The ticked options in menu order, for the header ("Geräte: 12 (HyDry Panels, Kauf)"). */
export function orderedDeviceListSelection(selection: DeviceListOption[]): DeviceListOption[] {
  return orderedSelection(selection, DEVICE_LIST_OPTIONS.map((entry) => entry.option));
}

/**
 * The ticked options for one cell of the Geräte card, so a click there opens the list showing
 * exactly the devices behind that number.
 *
 * @param column - the card's column: "panels" (the row total), a panel type 0 / 1 / 2, "comBoxes" or
 *   "minis".
 * @param row - an assignment type, "former" (Ehemalige Panels) or "total" (Gesamt).
 */
export function deviceListSelectionForCell(
  column: "panels" | 0 | 1 | 2 | "comBoxes" | "minis",
  row: DeviceCountAssignmentType | "former" | "total"
): DeviceListOption[] {
  const columnOption: DeviceListOption =
    column === "panels"
      ? DeviceKindOption.PANELS
      : column === "comBoxes"
        ? DeviceKindOption.BOXES
        : column === "minis"
          ? DeviceKindOption.MINIS
          : [PanelTypeOption.HYDRY_PANEL, PanelTypeOption.HYDRY_SLIM, PanelTypeOption.HYDRY_EDGE][column];

  const rowOption: DeviceListOption =
    row === "former" ? PeriodOption.FORMER : row === "total" ? PeriodOption.CURRENT : row;

  return [columnOption, rowOption];
}

/** The selection as one route query value (`filters=PANELS,SOLD`), and back; unknown values dropped. */
export function deviceListSelectionToQuery(selection: DeviceListOption[]): Record<string, string> {
  return selection.length > 0 ? { filters: orderedDeviceListSelection(selection).join(",") } : {};
}

export function deviceListSelectionFromQuery(query: Record<string, unknown>): DeviceListOption[] {
  if (typeof query.filters !== "string") {
    return [];
  }

  const known = DEVICE_LIST_OPTIONS.map((entry) => entry.option as string);
  return query.filters.split(",").filter((value) => known.includes(value)) as DeviceListOption[];
}

/** The `iresDeviceType` label key of a device row ("HyDry Box", "HyDry Slim", …), as the Geräte list names it. */
export function deviceTypeLabelKey(row: ClientDeviceRow): string {
  if (row.kind === "comBox") {
    return "iresDeviceType.box";
  }

  if (row.kind === "mini") {
    return "iresDeviceType.mini";
  }

  const key = ["panel", "slim", "edge"][row.panelType];
  return `iresDeviceType.${key ?? "panel"}`;
}

/**
 * The devices the Sensordaten-Export offers: HyDry Boxes and Panels. Minis are left out, as the
 * export has never covered them.
 */
export function isSensorExportDevice(row: ClientDeviceRow): boolean {
  return row.kind !== "mini";
}

/** The export page's filter options: the Geräte list's, without the Minis kind it never shows. */
export const SENSOR_EXPORT_OPTIONS: DeviceListOption[] = DEVICE_LIST_OPTIONS.map((entry) => entry.option).filter(
  (option) => option !== DeviceKindOption.MINIS
);
